import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import {
  getSeminarsAPI,
  getMyAssignedSeminarsAPI,
} from "../../../services/portal/student/studentSeminarService";
import { RoutePaths } from "../../../utility/RoutePaths";

const SeminarCard = ({ seminar, isPaid, onJoin }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
    {seminar.Image ? (
      <img
        src={seminar.Image}
        alt={seminar.Title}
        className="w-full h-40 object-cover"
      />
    ) : (
      <div className="w-full h-40 bg-primary-50 flex items-center justify-center">
        <svg
          className="w-12 h-12 text-primary-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      </div>
    )}

    <div className="p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-bold text-gray-900 line-clamp-2">
          {seminar.Title}
        </h3>
        {isPaid && (
          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
            Paid
          </span>
        )}
      </div>

      {seminar.Description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
          {seminar.Description}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
        {seminar.Date && (
          <div className="flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {new Date(seminar.Date).toLocaleDateString()}
          </div>
        )}
        {seminar.Fee !== undefined && seminar.Fee !== null && (
          <div className="flex items-center gap-1 font-semibold text-primary-600">
            <p className="text-primary-600">
              Rs. {seminar.Fee.toLocaleString(2)}
            </p>
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onJoin(seminar)}
            className="w-auto px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors bg-primary-600 text-white hover:bg-primary-700"
          >
            {isPaid ? "View Details" : "Join"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-12">
    <svg
      className="mx-auto w-14 h-14 text-gray-300 mb-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
    <p className="text-gray-500 font-medium">{message}</p>
  </div>
);

const SeminarsPage = () => {
  const navigate = useNavigate();
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingPaid, setLoadingPaid] = useState(false);
  const [allSeminars, setAllSeminars] = useState([]);
  const [paidSeminars, setPaidSeminars] = useState([]);

  useEffect(() => {
    loadAllSeminars();
    loadPaidSeminars();
  }, []);

  const loadAllSeminars = async () => {
    setLoadingAll(true);
    try {
      const res = await getSeminarsAPI();
      if (res && res.IsSuccessful && res.Data) {
        setAllSeminars(res.Data);
      }
    } catch (error) {
      console.error("Error loading seminars:", error);
      showErrorToastMsg("Failed to load seminars");
    } finally {
      setLoadingAll(false);
    }
  };

  const handleJoin = (seminar) => {
    navigate(
      `${RoutePaths.STUDENT_SEMINAR_DETAILS_PAGE}?seminarId=${seminar.SeminarId}`,
    );
  };

  const loadPaidSeminars = async () => {
    setLoadingPaid(true);
    try {
      const res = await getMyAssignedSeminarsAPI();
      if (res && res.IsSuccessful && res.Data) {
        setPaidSeminars(res.Data);
      }
    } catch (error) {
      console.error("Error loading assigned seminars:", error);
      showErrorToastMsg("Failed to load your seminars");
    } finally {
      setLoadingPaid(false);
    }
  };

  return (
    <DashboardLayout title="Seminars" subtitle="Browse and view your seminars">
      {/* My Seminars */}
      <div className="mb-10">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="w-1 h-5 bg-primary-600 rounded-full inline-block" />
          My Paid Seminars
        </h2>

        {loadingPaid ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : paidSeminars.length === 0 ? (
          <EmptyState message="You have not paid for any seminars yet." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {paidSeminars.map((seminar) => (
              <SeminarCard
                key={seminar.Id}
                seminar={seminar}
                isPaid={true}
                onJoin={handleJoin}
              />
            ))}
          </div>
        )}
      </div>

      {/* All Seminars */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="w-1 h-5 bg-gray-400 rounded-full inline-block" />
          All Seminars
        </h2>

        {loadingAll ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : allSeminars.length === 0 ? (
          <EmptyState message="No seminars available at the moment." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {allSeminars.map((seminar) => (
              <SeminarCard
                key={seminar.Id}
                seminar={seminar}
                isPaid={false}
                onJoin={handleJoin}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SeminarsPage;
